import { ConnectionQuality } from 'livekit-client'
import { cn } from '@/lib/utils'
import { toQualityLevel, type QualityLevel } from '@/types/network'

interface NetworkQualityIndicatorProps {
  quality: ConnectionQuality
  className?: string
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const qualityConfig: Record<
  QualityLevel,
  {
    bars: number
    color: string
    label: string
  }
> = {
  excellent: { bars: 3, color: 'bg-green-500', label: 'Excellent' },
  good: { bars: 2, color: 'bg-green-500', label: 'Good' },
  poor: { bars: 1, color: 'bg-yellow-500', label: 'Poor' },
  lost: { bars: 0, color: 'bg-red-500', label: 'Lost' },
  unknown: { bars: 0, color: 'bg-muted-foreground', label: 'Unknown' },
}

const sizeConfig = {
  sm: { width: 'w-0.5', gap: 'gap-px', heights: ['h-1', 'h-1.5', 'h-2'] },
  md: { width: 'w-1', gap: 'gap-0.5', heights: ['h-1.5', 'h-2.5', 'h-3.5'] },
  lg: { width: 'w-1.5', gap: 'gap-0.5', heights: ['h-2', 'h-3', 'h-4'] },
}

export function NetworkQualityIndicator({
  quality,
  className,
  showLabel = false,
  size = 'md',
}: NetworkQualityIndicatorProps) {
  const level = toQualityLevel(quality)
  const config = qualityConfig[level]
  const sizes = sizeConfig[size]

  return (
    <div className={cn('flex items-end', sizes.gap, className)}>
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className={cn(
            'rounded-sm transition-colors duration-200',
            sizes.width,
            sizes.heights[index],
            index < config.bars ? config.color : 'bg-muted-foreground/30'
          )}
        />
      ))}
      {showLabel && (
        <span className="text-xs text-muted-foreground ml-1.5">
          {config.label}
        </span>
      )}
    </div>
  )
}
